import { useMemo, useState } from 'react';
import { MagnifyingGlassIcon, TagIcon } from '@heroicons/react/24/outline';
import type { CatalogPermission } from '../../../services/iam';
import { PermName } from '../primitives/PermName';
import { AccessCard } from '../primitives/AccessCard';
import { AccessCardSubsection } from '../primitives/AccessCardSubsection';

const PERM_GROUPS_STORAGE_KEY = 'odo:access:perm-groups';

function familyLabel(group: string): string {
    return group.charAt(0).toUpperCase() + group.slice(1);
}

// Tolerates a missing/corrupt localStorage blob rather than breaking the page.
function loadOpenGroups(): Record<string, boolean> {
    if (typeof globalThis === 'undefined' || !globalThis.localStorage) return {};
    try {
        const raw = globalThis.localStorage.getItem(PERM_GROUPS_STORAGE_KEY);
        if (!raw) return {};
        const parsed: unknown = JSON.parse(raw);
        if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
            return parsed as Record<string, boolean>;
        }
        return {};
    } catch {
        return {};
    }
}

interface PermissionGroup {
    readonly family: string;
    readonly items: readonly CatalogPermission[];
}

function groupByFamily(permissions: readonly CatalogPermission[]): PermissionGroup[] {
    const groups = new Map<string, CatalogPermission[]>();
    for (const permission of permissions) {
        const bucket = groups.get(permission.group) ?? [];
        bucket.push(permission);
        groups.set(permission.group, bucket);
    }
    return Array.from(groups.entries())
        .map(([family, items]) => ({
            family,
            items: items.sort((a, b) => a.id.localeCompare(b.id))
        }))
        .sort((a, b) => a.family.localeCompare(b.family));
}

interface PermissionsListProps {
    permissions: readonly CatalogPermission[];
    selectedId: string | null;
    onSelect: (id: string) => void;
}

export function PermissionsList({ permissions, selectedId, onSelect }: PermissionsListProps) {
    const [search, setSearch] = useState('');
    // Open by default (short catalog); persisted so a manual fold survives reloads.
    const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(loadOpenGroups);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return permissions;
        return permissions.filter((p) => p.id.toLowerCase().includes(q));
    }, [permissions, search]);

    const groups = useMemo(() => groupByFamily(filtered), [filtered]);

    // While searching, every family renders open; derived, not synced into
    // `openGroups`, so manual fold state returns once search clears.
    const filtering = search.trim().length > 0;
    const isGroupOpen = (family: string) => filtering || openGroups[family] !== false;

    const toggleGroup = (family: string) => {
        setOpenGroups((prev) => {
            const next = { ...prev, [family]: !(prev[family] !== false) };
            try {
                globalThis.localStorage?.setItem(PERM_GROUPS_STORAGE_KEY, JSON.stringify(next));
            } catch {
                /* ignore */
            }
            return next;
        });
    };

    return (
        <div className="flex flex-col gap-3 h-full min-h-0">
            <div className="flex items-center h-9 bg-white border border-gray-200 rounded-lg px-3 shrink-0">
                <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 mr-2 shrink-0" />
                <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search permissions"
                    className="bg-transparent outline-none text-sm text-gray-700 placeholder-gray-400 w-full"
                />
            </div>
            <AccessCard
                icon={TagIcon}
                title="Permissions"
                count={filtered.length}
                grow
                noBodyPadding
                bodyClassName="flex-1 min-h-0 overflow-y-auto scrollbar-hide flex flex-col"
            >
                {groups.length === 0 && (
                    <p className="text-sm text-gray-400 px-3.5 py-4">
                        No permissions match “{search}”.
                    </p>
                )}
                {groups.map((group, index) => (
                    <AccessCardSubsection
                        key={group.family}
                        label={familyLabel(group.family)}
                        count={group.items.length}
                        divider={index > 0}
                        collapsible
                        open={isGroupOpen(group.family)}
                        onToggle={() => toggleGroup(group.family)}
                    >
                        <div className="flex flex-col gap-0.5 px-2 pb-2">
                            {group.items.map((permission) => (
                                <button
                                    key={permission.id}
                                    type="button"
                                    onClick={() => onSelect(permission.id)}
                                    aria-pressed={selectedId === permission.id}
                                    className={`text-left rounded-lg px-2 py-1.5 border transition-colors ${
                                        selectedId === permission.id
                                            ? 'bg-gray-100 border-gray-300'
                                            : 'border-transparent hover:bg-gray-50'
                                    }`}
                                >
                                    {/* showState off: repeated "no route" labels would read as wallpaper here. */}
                                    <PermName
                                        permission={permission.id}
                                        group={permission.group}
                                        wired={permission.routeCount > 0}
                                        showState={false}
                                    />
                                </button>
                            ))}
                        </div>
                    </AccessCardSubsection>
                ))}
            </AccessCard>
        </div>
    );
}
